/**
 * Presentation Layer - StatusEditDialog Component
 * ステータス編集ダイアログコンポーネント
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { Label as LabelIcon } from '@mui/icons-material';
import { STATUS_OPTIONS, MANUAL_INPUT_VALUE } from '../../../../domain/medicalReservation/EditableFieldsPolicy';
import { STATUS_COLORS, type StatusColor } from '../../../../domain/medicalReservation/ReservationStatus';

/**
 * Props定義
 */
export interface StatusEditDialogProps {
  /** ダイアログが開いているか */
  open: boolean;
  /** 現在の値 */
  currentValue: string;
  /** 保存時のコールバック */
  onSave: (value: string) => void;
  /** キャンセル時のコールバック */
  onCancel: () => void;
}

/**
 * MUIのカラーに変換
 */
const getMuiColor = (
  color: StatusColor
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (color) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'info':
      return 'info';
    case 'primary':
      return 'primary';
    case 'secondary':
      return 'secondary';
    default:
      return 'default';
  }
};

/**
 * ステータス編集ダイアログコンポーネント
 */
export const StatusEditDialog: React.FC<StatusEditDialogProps> = ({
  open,
  currentValue,
  onSave,
  onCancel,
}) => {
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [isManualInput, setIsManualInput] = useState(false);
  const [manualInputValue, setManualInputValue] = useState('');

  // ダイアログが開いたときに初期値を設定
  useEffect(() => {
    if (open) {
      // 現在の値が選択肢にあるかチェック
      const isExistingOption = STATUS_OPTIONS.some(
        (opt) => opt.value === currentValue || opt.label === currentValue
      );

      if (currentValue && !isExistingOption) {
        // 選択肢にない場合は手動入力モード
        setIsManualInput(true);
        setManualInputValue(currentValue);
        setSelectedValue('');
      } else {
        // 選択肢にある場合はそれを選択
        setIsManualInput(false);
        setManualInputValue('');
        setSelectedValue(currentValue);
      }
    }
  }, [open, currentValue]);

  /**
   * 選択変更ハンドラ
   */
  const handleSelectChange = useCallback(
    (event: { target: { value: string } }) => {
      const value = event.target.value;
      if (value === MANUAL_INPUT_VALUE) {
        setIsManualInput(true);
        setSelectedValue('');
      } else {
        setIsManualInput(false);
        setSelectedValue(value);
      }
    },
    []
  );

  /**
   * 手動入力変更ハンドラ
   */
  const handleManualInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setManualInputValue(event.target.value);
    },
    []
  );

  /**
   * 保存ハンドラ
   */
  const handleSave = useCallback(() => {
    const valueToSave = isManualInput ? manualInputValue : selectedValue;
    if (valueToSave.trim()) {
      onSave(valueToSave);
    }
  }, [isManualInput, manualInputValue, selectedValue, onSave]);

  /**
   * キャンセルハンドラ
   */
  const handleCancel = useCallback(() => {
    setSelectedValue('');
    setIsManualInput(false);
    setManualInputValue('');
    onCancel();
  }, [onCancel]);

  const isSaveDisabled = isManualInput
    ? !manualInputValue.trim()
    : !selectedValue;

  // 現在の値のカラー
  const currentColor = STATUS_COLORS[currentValue as keyof typeof STATUS_COLORS] ?? 'default';

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LabelIcon color="primary" />
        ステータスを選択
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* 現在の値 */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              現在の値:
            </Typography>
            {currentValue ? (
              <Chip
                label={currentValue}
                size="small"
                color={getMuiColor(currentColor)}
                variant="outlined"
              />
            ) : (
              <Typography variant="caption" color="text.disabled">
                未設定
              </Typography>
            )}
          </Box>

          {/* 選択 or 手動入力 */}
          {!isManualInput ? (
            <FormControl fullWidth size="small">
              <InputLabel id="status-select-label">ステータス</InputLabel>
              <Select
                labelId="status-select-label"
                value={selectedValue}
                label="ステータス"
                onChange={handleSelectChange}
                renderValue={(value) => {
                  const option = STATUS_OPTIONS.find((opt) => opt.value === value);
                  if (option) {
                    const color = STATUS_COLORS[option.value as keyof typeof STATUS_COLORS] ?? 'default';
                    return (
                      <Chip
                        label={option.label}
                        size="small"
                        color={getMuiColor(color)}
                        variant="filled"
                      />
                    );
                  }
                  return value;
                }}
              >
                {STATUS_OPTIONS.map((option) => {
                  const color = STATUS_COLORS[option.value as keyof typeof STATUS_COLORS] ?? 'default';
                  return (
                    <MenuItem key={option.value} value={option.value}>
                      <Chip
                        label={option.label}
                        size="small"
                        color={getMuiColor(color)}
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    </MenuItem>
                  );
                })}
                <MenuItem value={MANUAL_INPUT_VALUE}>
                  <em>手動入力...</em>
                </MenuItem>
              </Select>
            </FormControl>
          ) : (
            <Box>
              <TextField
                fullWidth
                size="small"
                label="ステータスを入力"
                value={manualInputValue}
                onChange={handleManualInputChange}
                autoFocus
                placeholder="例: カスタムステータス"
              />
              <Button
                size="small"
                onClick={() => setIsManualInput(false)}
                sx={{ mt: 1 }}
              >
                選択肢から選ぶ
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>キャンセル</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaveDisabled}
        >
          保存
        </Button>
      </DialogActions>
    </Dialog>
  );
};
